import React, { useState, useEffect } from "react";
import { useNavigate, Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { useAuth } from "@/contexts/AuthContext";
import { Eye, EyeOff, Mail, Lock } from "lucide-react";
import { motion } from "framer-motion";
import { toast } from "react-hot-toast";

const LoginPage: React.FC = () => {
	const [email, setEmail] = useState("");
	const [password, setPassword] = useState("");
	const [showPassword, setShowPassword] = useState(false);
	const [loading, setLoading] = useState(false);
	const [error, setError] = useState<string | null>(null);
	const navigate = useNavigate();
	const { signIn, user, profile } = useAuth();

	// Handle redirect after successful authentication
	useEffect(() => {
		if (user && profile) {
			switch (profile.role) {
				case "admin":
					navigate("/admin-dashboard");
					break;
				case "doula":
					if (!profile.onboarding_completed) {
						navigate("/doula-onboarding");
					} else {
						navigate("/doula-dashboard");
					}
					break;
				case "parent":
					navigate("/parent-dashboard");
					break;
				default:
					console.error("❌ LoginPage: Unknown role:", profile.role);
					break;
			}
		}
	}, [user, profile, navigate]);

	const handleSubmit = async (e: React.FormEvent) => {
		e.preventDefault();
		setLoading(true);
		setError(null);

		try {
			const { error } = await signIn(email, password);

			if (error) {
				console.error("❌ Login error:", error);
				setError(
					error.message || "Login failed. Please check your credentials.",
				);
				return;
			}

			toast.success("Login successful!");

			// The AuthContext will handle the user state and profile fetching
			// The useEffect above will handle the routing based on user role
		} catch (err) {
			console.error("Login error:", err);
			setError("An unexpected error occurred. Please try again.");
		} finally {
			setLoading(false);
		}
	};

	return (
		<div className="min-h-screen bg-gradient-to-br from-white via-gray-50 to-gray-100 flex items-center justify-center py-8 px-4">
			<div className="w-full max-w-6xl">
				<div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
					{/* Left Column - Form */}
					<motion.div
						initial={{ opacity: 0, x: -50 }}
						animate={{ opacity: 1, x: 0 }}
						transition={{ duration: 0.6 }}
						className="flex justify-center lg:justify-start"
					>
						<Card className="glass-card border-0 shadow-xl w-full max-w-md">
							<CardHeader className="text-center pb-4">
								<div className="flex items-center justify-center space-x-2 mb-4">
									<img
										src="/Gaia_Logo.svg"
										alt="Gaia"
										className="w-[130px] h-auto object-contain"
									/>
								</div>
								<CardTitle className="font-normal text-black font-abc-prophet text-4xl leading-tight lg:text-[32px]">
									Welcome Back
								</CardTitle>
								<p className="text-black font-karla leading-relaxed lg:text-xl lg:leading-8">
									Sign in to your account
								</p>
							</CardHeader>

							<CardContent className="space-y-4">
								{error && (
									<Alert variant="destructive">
										<AlertDescription>{error}</AlertDescription>
									</Alert>
								)}

								<form onSubmit={handleSubmit} className="space-y-4">
									{/* Email */}
									<div>
										<Label
											htmlFor="email"
											className="block text-sm lg:text-xl leading-normal text-black font-karla mb-2 font-normal"
										>
											Email Address
										</Label>
										<div className="relative mt-1">
											<Mail className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 h-5 w-5" />
											<Input
												id="email"
												type="email"
												placeholder="Enter your email"
												value={email}
												onChange={(e) => setEmail(e.target.value)}
												className="pl-10 text-base h-auto shadow-btn-shadow py-[12px] bg-white border-newTheme-lightGrey rounded-md"
												required
											/>
										</div>
									</div>

									{/* Password */}
									<div>
										<Label
											htmlFor="password"
											className="block text-sm lg:text-xl leading-normal text-black font-karla mb-2 font-normal"
										>
											Password
										</Label>
										<div className="relative mt-1">
											<Lock className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 h-5 w-5" />
											<Input
												id="password"
												type={showPassword ? "text" : "password"}
												placeholder="Enter your password"
												value={password}
												onChange={(e) => setPassword(e.target.value)}
												className="pl-10 pr-10 text-base h-auto shadow-btn-shadow py-[12px] bg-white border-newTheme-lightGrey rounded-md"
												required
											/>
											<Button
												type="button"
												variant="ghost"
												size="sm"
												className="absolute right-0 top-0 h-full px-3 py-2 hover:bg-transparent"
												onClick={() => setShowPassword(!showPassword)}
											>
												{showPassword ? (
													<EyeOff className="h-4 w-4 text-gray-400" />
												) : (
													<Eye className="h-4 w-4 text-gray-400" />
												)}
											</Button>
										</div>
									</div>

									<Button
										type="submit"
										disabled={loading}
										className="w-full text-black py-[14px] px-[20px] shadow-btn-shadow font-karla text-[20px] h-auto mt-4 bg-newTheme-lime"
									>
										{loading ? (
											<div className="flex items-center">
												<div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
												Signing In...
											</div>
										) : (
											"Sign In"
										)}
									</Button>
								</form>

								<div className="text-center">
									<p className="text-sm text-black font-karla">
										Don't have an account?
									</p>
									<p className="text-sm text-black font-karla mt-1">
										Sign Up: &nbsp;
										<Link
											to="/parent-signup"
											className="text-black hover:text-primary-700 p-0 h-auto font-semibold"
										>
											Parent
										</Link>
										{" or "}
										<Link
											to="/doula-signup"
											className="text-black hover:text-primary-700 p-0 h-auto font-semibold"
										>
											Doula
										</Link>
									</p>
								</div>
							</CardContent>
						</Card>
					</motion.div>

					{/* Right Column - Image */}
					<motion.div
						initial={{ opacity: 0, x: 50 }}
						animate={{ opacity: 1, x: 0 }}
						transition={{ duration: 0.8, delay: 0.2 }}
						className="flex justify-center lg:justify-end"
					>
						<div className="relative">
							<img
								src="/Gaia_Hero.png"
								alt="Pregnant woman with doula support"
								className="w-full h-auto max-w-lg object-cover aspect-[4/5]"
							/>
						</div>
					</motion.div>
				</div>
			</div>
		</div>
	);
};

export default LoginPage;
